/**
 * @file vnc.tsx
 * @description Página de monitoramento VNC. Lista os containers/hosts que estão
 * online (recebidos em tempo real pelo canal Phoenix `vnc:`) e permite visualizar
 * suas telas remotas em dois modos:
 *
 * - **Modo Único (`single`)**: exibe uma única tela VNC em destaque (host focado).
 * - **Modo Grade (`grid`)**: exibe várias telas simultaneamente, respeitando o
 *   limite de {@link GRID_LIMIT} hosts.
 *
 * Cada tela é renderizada pelo componente {@link VncTile}.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, IconButton, List, ListItem, ListItemIcon, ListItemText, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import socket from "../../socket";
import { AppStore } from '../../stores/AppStore';
import { copyToClipboard } from '../../utils';
import { AppIcon } from '../utils/AppIcon';
import VncTile, { ConnStatus, TileControls, VncHost } from './VncTile';

/** Canal Phoenix responsável por publicar os hosts VNC que entram/saem (`online`). */
const socket_vnc = socket.channel("vnc:");
const ScreenSizeDetector = require('screen-size-detector');
const screen = new ScreenSizeDetector();

socket_vnc.join()
  .receive("ok", resp => {
    console.log("Bem vindo 1", resp)
  })
  .receive("error", resp => {
    console.log("Error", resp)
  })

/** Cor associada a cada estado de conexão, usada no ícone do host na lista lateral. */
const STATUS_COLOR: Record<ConnStatus, string> = {
  connecting: "#f5a623",
  connected: "#2E8B57",
  idle: "#888",
};

/** Número máximo de hosts que podem ser exibidos simultaneamente no Modo Grade. */
const GRID_LIMIT = 500;

/**
 * Componente de página do monitoramento VNC.
 *
 * Mantém o estado dos hosts online, do modo de visualização (único/grade), do host
 * em foco e da seleção da grade, além de orquestrar as conexões VNC delegadas aos
 * {@link VncTile}.
 *
 * @returns Elemento React com a lista lateral de containers e a área de visualização.
 */
const VncPage = ({ }) => {

  const [hostsOnline, setHostsOnline] = useState<VncHost[]>([]);
  const [search, setSearch] = useState("");
  const [viewOnly, setViewOnly] = useState(true);
  const [mode, setMode] = useState<"grid" | "single">("single");
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);
  const [statusMap, setStatusMap] = useState<Record<string, ConnStatus>>({});
  const [disconnectedIds, setDisconnectedIds] = useState<string[]>([]);
  const [selectedGridIds, setSelectedGridIds] = useState<string[]>([]);

  /** Espelho de `focusedTaskId` acessível dentro de callbacks do socket sem recriá-los. */
  const focusedRef = useRef<string | null>(null);
  /** Controles imperativos (ex.: fullscreen) registrados por cada {@link VncTile}, indexados por `task_id`. */
  const controlsRef = useRef<Record<string, TileControls>>({});

  // Mantém `focusedRef` sincronizado com o estado para uso dentro do listener do socket.
  useEffect(() => { focusedRef.current = focusedTaskId; }, [focusedTaskId]);

  /**
   * Inscreve-se no evento `online` do canal VNC ao montar a página: adiciona hosts
   * que ficam online (`status === 1`) e remove os que saem, limpando também qualquer
   * referência a eles (foco, desconectados e seleção da grade).
   */
  useEffect(() => {
    AppStore.setTitle("VNC");

    socket_vnc.on("online", payload => {

      console.log("online: ", payload)

      setHostsOnline((prevHosts) => {

        const exists = prevHosts.some((host) => host.task_id === payload.task_id);

        if (payload.status === 1) {

          return exists ? prevHosts : [...prevHosts, payload];

        }

        if (focusedRef.current === payload.task_id) {

          setFocusedTaskId(null);

        }

        setDisconnectedIds((prev) => prev.filter((id) => id !== payload.task_id));

        setSelectedGridIds((prev) => prev.filter((id) => id !== payload.task_id));

        return prevHosts.filter((host) => host.task_id !== payload.task_id);

      });

    })

    return () => {

      socket_vnc.off("online");

    };

  }, []);

  /**
   * Atualiza o estado de conexão de um host na lista lateral.
   *
   * @param taskId Identificador do host.
   * @param status Novo estado de conexão, ou `null` para remover o host do mapa.
   */
  const handleStatus = useCallback((taskId: string, status: ConnStatus | null) => {

    setStatusMap((prev) => {

      if (status === null) {

        if (!(taskId in prev)) return prev;
        const next = { ...prev };
        delete next[taskId];
        return next;
      }

      if (prev[taskId] === status) return prev;

      return { ...prev, [taskId]: status };

    });

  }, []);

  /**
   * Registra (ou remove) os controles imperativos de um {@link VncTile}.
   *
   * @param taskId Identificador do host.
   * @param controls Controles do tile, ou `null` para desregistrá-los.
   */
  const registerControls = useCallback((taskId: string, controls: TileControls | null) => {

    if (controls) controlsRef.current[taskId] = controls;

    else delete controlsRef.current[taskId];

  }, []);

  /**
   * Foca um host e alterna para o Modo Único.
   *
   * @param host Host a ser exibido em destaque.
   */
  const focusHost = (host: VncHost) => {

    setFocusedTaskId(host.task_id);

    setMode("single");

  };

  /**
   * Alterna a presença de um host na seleção da grade. Remove se já estiver
   * selecionado; caso contrário adiciona, respeitando o {@link GRID_LIMIT}
   * (exibindo um aviso quando o limite é atingido).
   *
   * @param host Host cujo estado de seleção será alternado.
   */
  const toggleGridSelection = (host: VncHost) => {

    setSelectedGridIds((prev) => {

      if (prev.includes(host.task_id)) {

        return prev.filter((id) => id !== host.task_id);

      }

      if (prev.length >= GRID_LIMIT) {

        toast.warning(`Você pode exibir no máximo ${GRID_LIMIT} hosts no Modo Grade.`);
        return prev;

      }

      return [...prev, host.task_id];

    });

  };

  /**
   * Manipula o clique em um host na lista lateral: alterna a seleção da grade no
   * Modo Grade ou foca o host no Modo Único.
   *
   * @param host Host clicado.
   */
  const handleHostClick = (host: VncHost) => {

    if (mode === "grid") toggleGridSelection(host);

    else focusHost(host);

  };

  /**
   * Seleciona todos os hosts atualmente filtrados para o Modo Grade, limitando a
   * seleção aos primeiros {@link GRID_LIMIT} hosts (com aviso quando há excedente).
   */
  const selectAllGrid = () => {

    const ids = filteredHosts.map((host) => host.task_id);

    if (ids.length > GRID_LIMIT) {

      toast.warning(`Você pode exibir no máximo ${GRID_LIMIT} hosts no Modo Grade. Selecionando os primeiros ${GRID_LIMIT}.`);

    }

    setSelectedGridIds(ids.slice(0, GRID_LIMIT));

  };

  /** Remove todos os hosts da seleção da grade. */
  const clearGridSelection = () => setSelectedGridIds([]);

  /**
   * Alterna entre os modos Único e Grade. Ao entrar no Modo Grade, limpa o host
   * focado.
   */
  const toggleMode = () => {

    if (mode === "grid") {

      setMode("single");

    } else {

      setFocusedTaskId(null);

      setMode("grid");

    }

  };

  /**
   * Alterna a conexão de um tile na grade (conectado ⇄ desconectado), controlando
   * se o respectivo {@link VncTile} deve manter a sessão VNC ativa.
   *
   * @param host Host cuja conexão será alternada.
   */
  const toggleConnection = (host: VncHost) => {

    setDisconnectedIds((prev) =>

      prev.includes(host.task_id)
        ? prev.filter((id) => id !== host.task_id)
        : [...prev, host.task_id]

    );

  };

  /**
   * Coloca o host focado em tela cheia através dos controles registrados; exibe
   * um aviso caso nenhum host esteja focado.
   */
  const handleFullscreen = () => {

    if (focusedTaskId && controlsRef.current[focusedTaskId]) controlsRef.current[focusedTaskId].fullscreen();

    else toast.info("Selecione um host para usar tela cheia.");

  };

  /**
   * Copia o `task_id` informado para a área de transferência e notifica o usuário.
   *
   * @param token Identificador (task id) a ser copiado.
   */
  const copyChave = (token: string) => {

    copyToClipboard(token);
    toast.success("Task ID copiada com sucesso!", {});

  }

  /** Hosts online filtrados pelo termo de busca (hostname, porta, task id ou ip) e ordenados pelo IP. */
  const filteredHosts = hostsOnline.filter((host) =>
      `${host.hostname}:${host.port} ${host.task_id} ${host.ip}`.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => Number(a.ip.replace(/\D/g, "")) - Number(b.ip.replace(/\D/g, "")));

  /** Host atualmente focado no Modo Único, ou `null` se nenhum estiver selecionado. */
  const focusedHost = hostsOnline.find((h) => h.task_id === focusedTaskId) || null;

  /** Hosts selecionados para exibição no Modo Grade. */
  const gridHosts = hostsOnline.filter((host) => selectedGridIds.includes(host.task_id))
    .sort((a, b) => Number(a.ip.replace(/\D/g, "")) - Number(b.ip.replace(/\D/g, "")));;

  return (

    <div style={{ display: "flex" }}>

      <div style={{ textAlign: "left", minWidth: 420 }}>

        <ListItem style={{ paddingLeft: 0, display: 'flex', justifyContent: 'space-between' }}>
          <ListItemText><b>Containers ({filteredHosts.length})</b></ListItemText>
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            {mode === "grid"
              ? `Monitorando ${selectedGridIds.length} de ${GRID_LIMIT}`
              : ""
            }
          </span>
        </ListItem>

        <ListItem style={{ padding: 0, paddingRight: 5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar host..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="filled"
          />
        </ListItem>

        {mode === "grid" && (
          <ListItem style={{ padding: "8px 5px 8px 0", display: "flex", gap: 8 }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<AppIcon size={18} name="select_all" />}
              onClick={selectAllGrid}
              disabled={filteredHosts.length === 0}
            >
              Selecionar todos
            </Button>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<AppIcon size={18} name="deselect" />}
              onClick={clearGridSelection}
              disabled={selectedGridIds.length === 0}
            >
              Remover todos
            </Button>
          </ListItem>
        )}

        <List component="div" disablePadding sx={{ padding: "0px 10px 0px 0px", overflow: "auto", maxHeight: (screen.height - 380) }}>

          {filteredHosts.map(value => {

            const isFocused = focusedTaskId === value.task_id;
            const isSelected = selectedGridIds.includes(value.task_id);
            const st = statusMap[value.task_id];

            return (
              <ListItem key={value.task_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(204, 204, 204, 0.13)" }}>
                <span
                  onClick={() => handleHostClick(value)}
                  className="list-item-bot"
                >
                  <ListItemIcon style={{ minWidth: 30, display: "flex", alignItems: "center" }}>
                    {mode === "grid" ? (
                      <AppIcon
                        size={18}
                        name={isSelected ? "check_box" : "check_box_outline_blank"}
                        style={{ color: isSelected ? "#3579ea" : "#9aa0a6" }}
                      />
                    ) : isFocused ? (
                      <AppIcon size={18} name="live_tv" style={{ color: "#3579ea" }} />
                    ) : (
                      <AppIcon
                        size={18}
                        name="tv"
                        color={st ? STATUS_COLOR[st] : "#9aa0a6"}
                        className={st === "connecting" ? "vnc-pulse" : ""}
                      />
                    )}
                  </ListItemIcon>

                  <ListItemText>
                    <span style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                      {value.hostname}:{value.port}<br />{value.task_id}<br />{value.ip}
                    </span>
                  </ListItemText>
                </span>

                <IconButton
                  onClick={() => copyChave(value.task_id)}
                  style={{ marginLeft: "auto" }}
                >
                  <AppIcon name="content_paste" size={18} style={{ color: "#3579ea" }} />
                </IconButton>

              </ListItem>
            );
          })}
        </List>

      </div>

      <div style={{ width: "100%", textAlign: "right" }}>

        <div style={{
          marginBottom: 10,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "flex-end",
          justifyContent: "flex-end",
          width: "100%"
        }}>

          <Button
            variant="outlined"
            startIcon={<AppIcon size={20} name={mode === "grid" ? "crop_square" : "grid_view"} />}
            onClick={toggleMode}

          >
            {mode === "grid" ? "Modo Único" : "Modo Grade"}
          </Button>

          <Button
            variant="outlined"
            startIcon={viewOnly ? <AppIcon size={20} name="check_box_outline_blank" /> : <AppIcon size={20} name="check_box" />}
            onClick={() => setViewOnly(!viewOnly)}
            disabled={mode === "single" && !focusedTaskId}

            className={viewOnly ? "" : "btn-liberado-vnc"}
          >
            {viewOnly ? "Liberar VNC" : "VNC Liberado"}
          </Button>

          <Button
            variant="outlined"
            startIcon={<AppIcon size={20} name="desktop_access_disabled" />}
            onClick={() => setFocusedTaskId(null)}
            disabled={mode === "grid" || !focusedTaskId}

          >
            Desconectar
          </Button>

          <Button
            variant="outlined"
            startIcon={<AppIcon size={20} name="fullscreen" />}
            onClick={handleFullscreen}
          >
            Fullscreen
          </Button>

        </div>

        {mode === "single" ? (
          focusedHost ? (
            <VncTile
              key={focusedHost.task_id}
              host={focusedHost}
              viewOnly={viewOnly}
              focused
              compact={false}
              canvasHeight={screen.height - 380}
              onStatus={handleStatus}
              registerControls={registerControls}
            />
          ) : (
            <div className="vnc-placeholder" style={{ height: (screen.height - 330) }}>
              <AppIcon name="touch_app" size={40} style={{ color: "#bbb" }} />
              <span>Selecione um VNC na lista ao lado para monitorar.</span>
            </div>
          )
        ) : gridHosts.length === 0 ? (
          <div className="vnc-placeholder" style={{ height: (screen.height - 330) }}>
            <AppIcon name="touch_app" size={40} style={{ color: "#bbb" }} />
            <span>Selecione os containers na lista ao lado para exibir na grade (até {GRID_LIMIT}).</span>
          </div>
        ) : (
          <div className="vnc-grid" style={{ maxHeight: (screen.height - 330) }}>
            {gridHosts.map((host) => (
              <VncTile
                key={host.task_id}
                host={host}
                viewOnly
                compact
                active={!disconnectedIds.includes(host.task_id)}
                canvasHeight={200}
                onSelect={focusHost}
                onStatus={handleStatus}
                registerControls={registerControls}
                onToggleConnection={toggleConnection}
              />
            ))}
          </div>
        )}

      </div>

    </div>

  );

}

export default VncPage;
